import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Gauge, Euro, Newspaper, TrendingUp, Building2, Sparkles, Layers, HelpCircle, Info, AlertCircle, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { sentimentLabels } from '../lib/data';
import { getAnalysis, WidgetRationale, sentimentColorClass, confidenceLabel, confidenceBadgeClass } from '../lib/predictive';
function WidgetCard({
  icon: Icon,
  title,
  rationale,
  children,
  className = ''
}: {
  icon: React.ElementType;
  title: string;
  rationale: WidgetRationale;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className={`bg-card border border-border rounded-lg p-6 flex flex-col ${className}`} data-id="element-1106">
      <div className="flex items-start justify-between gap-2 mb-4" data-id="element-1107">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-heading uppercase tracking-wider" data-id="element-1108">
          <Icon className="h-3.5 w-3.5" data-id="element-1109" />
          {title}
        </div>
        <button onClick={() => setOpen(true)} aria-label={`Perché questa previsione: ${title}`} className="text-muted-foreground/60 hover:text-accent transition-colors -mt-0.5 -mr-1 p-1 rounded-full hover:bg-accent/10" data-id="element-1110">
          <HelpCircle className="h-4 w-4" data-id="element-1111" />
        </button>
      </div>
      <div className="flex-1" data-id="element-1112">{children}</div>

      <Dialog open={open} onOpenChange={setOpen} data-id="element-1113">
        <DialogContent data-id="element-1114">
          <DialogHeader data-id="element-1115">
            <DialogTitle className="flex items-center gap-2" data-id="element-1116">
              <Info className="h-4 w-4 text-accent" data-id="element-1117" />
              {title} — perché questa previsione
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed pt-1" data-id="element-1118">
              {rationale.why}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2" data-id="element-1119">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading mb-2" data-id="element-1120">
              Su quali dati si basa
            </div>
            <ul className="space-y-2" data-id="element-1121">
              {rationale.dataBasis.map((d, i) => <li key={i} className="flex items-start gap-2 text-sm text-foreground" data-id="element-1122">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" data-id="element-1123" />
                  {d}
                </li>)}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}
export function PredictiveAnalysisDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const analysis = id ? getAnalysis(id) : undefined;
  if (!analysis) {
    return <div className="px-6 py-10 max-w-[1400px] mx-auto w-full" data-id="element-1124">
        <Button variant="ghost" onClick={() => navigate('/predictive')} className="mb-6 -ml-2" data-id="element-1125">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1126" />
          Torna alle analisi
        </Button>
        <div className="bg-card border border-border rounded-lg p-12 text-center text-sm text-muted-foreground" data-id="element-1127">
          Analisi non trovata.
        </div>
      </div>;
  }
  const tierChart = analysis.reachByTier.map(t => ({
    label: t.label,
    value: t.projectedArticles
  }));
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-1128">
      {/* Back */}
      <Button variant="ghost" onClick={() => navigate('/predictive')} className="-ml-2" data-id="element-1129">
        <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1130" />
        Torna alle analisi
      </Button>

      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-1131">
        <div className="min-w-0" data-id="element-1132">
          <div className="flex items-center gap-2 text-sm text-muted-foreground font-heading mb-2" data-id="element-1133">
            <FileText className="h-4 w-4" data-id="element-1134" />
            Analisi predittiva
          </div>
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight leading-snug" data-id="element-1135">
            {analysis.fileName}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-muted-foreground" data-id="element-1136">
            <span data-id="element-1137">
              {new Date(analysis.createdAt).toLocaleDateString('it-IT', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
            </span>
            <span data-id="element-1138">·</span>
            <span data-id="element-1139">{analysis.fileSize}</span>
          </div>
        </div>
        <Badge variant="secondary" className={`flex-shrink-0 text-xs ${confidenceBadgeClass(analysis.confidence)}`} data-id="element-1140">
          Confidenza {confidenceLabel(analysis.confidence)}
        </Badge>
      </header>

      {/* Detected themes */}
      {analysis.themes.length > 0 && <section className="flex flex-wrap items-center gap-2" data-id="element-1141">
          <span className="text-xs text-muted-foreground font-heading uppercase tracking-wider mr-1" data-id="element-1142">
            Temi rilevati
          </span>
          {analysis.themes.map(t => <Badge key={t} variant="secondary" className="bg-accent/10 text-accent font-normal" data-id="element-1143">
              {t}
            </Badge>)}
        </section>}

      {/* Metrics widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1144">
        <WidgetCard icon={Gauge} title="Score previsto" rationale={analysis.rationales.score} data-id="element-1145">
          <div className="flex items-baseline gap-2" data-id="element-1146">
            <span className="text-4xl font-light text-accent leading-none" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-1147">
              {analysis.predictedScore.toFixed(1)}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-1148">/10</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden mt-4" data-id="element-1149">
            <div className="h-full bg-accent rounded-full" style={{
            width: `${analysis.predictedScore / 10 * 100}%`
          }} data-id="element-1150" />
          </div>
        </WidgetCard>

        <WidgetCard icon={Euro} title="AVE stimato" rationale={analysis.rationales.ave} data-id="element-1151">
          <div className="text-2xl font-light leading-none" style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-1152">
            €{(analysis.aveLow / 1000).toFixed(0)}k – €
            {(analysis.aveHigh / 1000).toFixed(0)}k
          </div>
          <p className="text-xs text-muted-foreground mt-3" data-id="element-1153">
            Intervallo di stima
          </p>
        </WidgetCard>

        <WidgetCard icon={Newspaper} title="Articoli previsti" rationale={analysis.rationales.articles} data-id="element-1154">
          <div className="text-4xl font-light leading-none" style={{
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-1155">
            ~{analysis.projectedArticles}
          </div>
          <p className="text-xs text-muted-foreground mt-3" data-id="element-1156">Uscite stimate</p>
        </WidgetCard>

        <WidgetCard icon={TrendingUp} title="Sentiment atteso" rationale={analysis.rationales.sentiment} data-id="element-1157">
          <div className="flex items-center gap-2" data-id="element-1158">
            <span className={`w-3 h-3 rounded-full ${sentimentColorClass(analysis.expectedSentiment)}`} data-id="element-1159" />
            <span className="text-xl font-normal" data-id="element-1160">
              {sentimentLabels[analysis.expectedSentiment]}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-3" data-id="element-1161">
            Confidenza {analysis.sentimentConfidence}%
          </p>
        </WidgetCard>
      </div>

      {/* Tier reach + sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1162">
        <WidgetCard icon={Layers} title="Copertura prevista per tier" rationale={analysis.rationales.tier} data-id="element-1163">
          <div className="h-56 mt-2" data-id="element-1164">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1165">
              <BarChart data={tierChart} margin={{
              top: 5,
              right: 0,
              bottom: 0,
              left: -20
            }} data-id="element-1166">
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1167" />
                <XAxis dataKey="label" tick={{
                fontSize: 12
              }} stroke="#8B8680" axisLine={false} tickLine={false} dy={8} data-id="element-1168" />
                <YAxis tick={{
                fontSize: 12
              }} stroke="#8B8680" axisLine={false} tickLine={false} allowDecimals={false} data-id="element-1169" />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={48} data-id="element-1170">
                  {tierChart.map((_, i) => <Cell key={i} fill="#0187c3" data-id="element-1171" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </WidgetCard>

        <WidgetCard icon={Building2} title="Testate più probabili" rationale={analysis.rationales.sources} data-id="element-1172">
          <div className="flex flex-wrap gap-2 mt-1" data-id="element-1173">
            {analysis.likelySources.map(s => <Badge key={s} variant="secondary" className="bg-muted text-foreground font-normal" data-id="element-1174">
                {s}
              </Badge>)}
          </div>
        </WidgetCard>
      </div>

      {/* Drivers */}
      <section className="bg-card border border-border rounded-lg p-6" data-id="element-1175">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-heading uppercase tracking-wider mb-4" data-id="element-1176">
          <Sparkles className="h-3.5 w-3.5" data-id="element-1177" />
          Fattori della previsione
        </div>
        <ul className="space-y-2.5" data-id="element-1178">
          {analysis.drivers.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-foreground leading-relaxed" data-id="element-1179">
              <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" data-id="element-1180" />
              {d}
            </li>)}
        </ul>
      </section>

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-accent/5 border border-border rounded-lg" data-id="element-1181">
        <AlertCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" data-id="element-1182" />
        <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-1183">
          Previsione stimata sui dati storici delle rassegne e sui temi rilevati
          nel comunicato. È un'indicazione probabilistica, non una garanzia di
          copertura: i risultati reali dipendono dal contesto mediatico e dalla
          tempistica della diffusione. Usa il pulsante accanto a ogni widget per
          capire come è stata calcolata ciascuna stima.
        </p>
      </div>
    </div>;
}